import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Image as ChakraImage,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.2;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const ImagePreviewZoom = memo(
  ({ src, alt = "preview-image", children, previewTitle = "Xem ảnh" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);

    const dragStateRef = useRef({
      startX: 0,
      startY: 0,
      initialX: 0,
      initialY: 0,
    });

    const openPreview = useCallback(() => {
      if (!src) return;
      setIsOpen(true);
    }, [src]);

    const closePreview = useCallback(() => {
      setIsOpen(false);
    }, []);

    const resetTransform = useCallback(() => {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }, []);

    useEffect(() => {
      if (isOpen) {
        resetTransform();
      }
    }, [isOpen, resetTransform]);

    const zoomIn = useCallback(() => {
      setScale((prev) =>
        clamp(Number((prev + SCALE_STEP).toFixed(2)), MIN_SCALE, MAX_SCALE),
      );
    }, []);

    const zoomOut = useCallback(() => {
      setScale((prev) => {
        const next = clamp(
          Number((prev - SCALE_STEP).toFixed(2)),
          MIN_SCALE,
          MAX_SCALE,
        );
        if (next === 1) {
          setPosition({ x: 0, y: 0 });
        }
        return next;
      });
    }, []);

    const handleWheel = useCallback((event) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.deltaY < 0) {
        setScale((prev) =>
          clamp(Number((prev + SCALE_STEP).toFixed(2)), MIN_SCALE, MAX_SCALE),
        );
      } else {
        setScale((prev) => {
          const next = clamp(
            Number((prev - SCALE_STEP).toFixed(2)),
            MIN_SCALE,
            MAX_SCALE,
          );
          if (next === 1) {
            setPosition({ x: 0, y: 0 });
          }
          return next;
        });
      }
    }, []);

    const handleMouseDown = useCallback(
      (event) => {
        if (scale <= 1) return;

        event.preventDefault();
        setIsDragging(true);

        dragStateRef.current = {
          startX: event.clientX,
          startY: event.clientY,
          initialX: position.x,
          initialY: position.y,
        };
      },
      [position.x, position.y, scale],
    );

    const handleMouseMove = useCallback(
      (event) => {
        if (!isDragging || scale <= 1) return;

        const deltaX = event.clientX - dragStateRef.current.startX;
        const deltaY = event.clientY - dragStateRef.current.startY;

        setPosition({
          x: dragStateRef.current.initialX + deltaX,
          y: dragStateRef.current.initialY + deltaY,
        });
      },
      [isDragging, scale],
    );

    const stopDragging = useCallback(() => {
      setIsDragging(false);
    }, []);

    return (
      <>
        <Box onClick={openPreview} cursor={src ? "pointer" : "default"} w={"100%"} h={"100%"}>
          {children}
        </Box>

        <Modal isOpen={isOpen} onClose={closePreview} size="full" isCentered>
          <ModalOverlay bg="rgba(0, 0, 0, 0.75)" />
          <ModalContent
            bg="rgba(17, 24, 39, 0.96)"
            m="0"
            maxW="100vw"
            minH="100vh"
            borderRadius="0"
            overflow="hidden"
          >
            <ModalBody p="0" position="relative">
              <Flex
                position="absolute"
                top="16px"
                left="20px"
                right="20px"
                zIndex={3}
                justifyContent="space-between"
                alignItems="center"
                gap="12px"
              >
                <Text
                  color="white"
                  fontSize="16px"
                  fontWeight="600"
                  noOfLines={1}
                >
                  {previewTitle}
                </Text>

                <Flex
                  alignItems="center"
                  gap="8px"
                  bg="rgba(255, 255, 255, 0.08)"
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="12px"
                  p="6px"
                >
                  <IconButton
                    aria-label="Thu nhỏ ảnh"
                    icon={<Text fontSize="18px">−</Text>}
                    onClick={zoomOut}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "rgba(255,255,255,0.12)" }}
                  />
                  <Text
                    minW="56px"
                    textAlign="center"
                    color="white"
                    fontSize="14px"
                    fontWeight="500"
                  >
                    {Math.round(scale * 100)}%
                  </Text>
                  <IconButton
                    aria-label="Phóng to ảnh"
                    icon={<Text fontSize="18px">+</Text>}
                    onClick={zoomIn}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "rgba(255,255,255,0.12)" }}
                  />
                  <IconButton
                    aria-label="Đặt lại ảnh"
                    icon={<Text fontSize="13px">1:1</Text>}
                    onClick={resetTransform}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "rgba(255,255,255,0.12)" }}
                  />
                  <IconButton
                    aria-label="Đóng ảnh"
                    icon={<Text fontSize="18px">✕</Text>}
                    onClick={closePreview}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "rgba(255,255,255,0.12)" }}
                  />
                </Flex>
              </Flex>

              <Flex
                w="100%"
                h="100vh"
                justifyContent="center"
                alignItems="center"
                overflow="hidden"
                onWheel={handleWheel}
                onMouseMove={handleMouseMove}
                onMouseUp={stopDragging}
                onMouseLeave={stopDragging}
                cursor={
                  scale > 1 ? (isDragging ? "grabbing" : "grab") : "default"
                }
                userSelect="none"
              >
                <Box
                  onMouseDown={handleMouseDown}
                  transition={isDragging ? "none" : "transform 0.2s ease"}
                  transform={`translate(${position.x}px, ${position.y}px) scale(${scale})`}
                  transformOrigin="center center"
                  maxW="92vw"
                  maxH="84vh"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ChakraImage
                    src={src}
                    alt={alt}
                    maxW="92vw"
                    maxH="84vh"
                    objectFit="contain"
                    draggable={false}
                    userSelect="none"
                    pointerEvents="none"
                  />
                </Box>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  },
);

export default ImagePreviewZoom;
